import { Card, CardContent } from '@client/components/ui/card';
import { Alert, AlertDescription } from '@client/components/ui/alert';
import { Button } from '@client/components/ui/button';
import { ContactSupportButton } from '@client/components/ContactSupportButton';
import { RefreshCw, Loader2 } from 'lucide-react';
import { useSubscriptionActions } from './useSubscriptionActions';
import { SubscriptionState } from '@client/model';
import { SessionType } from '@shared';

function Metric({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="min-w-[120px]">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-xl font-semibold text-primary-dark">{value}</p>
    </div>
  );
}

interface NonOwnerOverviewCardProps {
  ownerInfo: {
    advisorName: string | null;
    organization: string | null;
  } | null;
  currentAdvisorAssignedDateTime: Date | null;
  seatStatus?: string | null;
  sessionType: SessionType;
  subscription: SubscriptionState | null;
}

export function NonOwnerOverviewCard({
  ownerInfo,
  currentAdvisorAssignedDateTime,
  seatStatus,
  sessionType,
  subscription,
}: NonOwnerOverviewCardProps) {
  const ownerName = ownerInfo?.advisorName || '—';
  const organization = ownerInfo?.organization || '—';
  const joinedDate = currentAdvisorAssignedDateTime
    ? new Date(currentAdvisorAssignedDateTime).toLocaleDateString()
    : '—';

  const { handleCreateSubscription, isLoadingCheckout } = useSubscriptionActions({
    subscription: subscription || ({} as SubscriptionState),
  });

  // Format seat status for display
  const formatSeatStatus = (status: string | null | undefined): string => {
    if (!status) return 'unknown';
    return status.includes('_') ? status.split('_').join(' ') : status;
  };

  // Get badge styling based on seat status
  const getSeatStatusBadgeClass = (status: string | null | undefined): string => {
    if (!status) return 'bg-gray-100 text-gray-700';

    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'inactive':
      case 'payment_failed':
        return 'bg-red-100 text-red-700';
      case 'invited':
        return 'bg-blue-100 text-blue-700';
      case 'paused':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const showCreateSubscriptionButton = sessionType === SessionType.advisorInactiveSubscription;

  return (
    <Card className="mt-14 max-w-[900px] border border-primary-200">
      <CardContent className="flex flex-col gap-5 p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-primary-dark">Subscription Information</h2>
          {seatStatus && (
            <span
              className={`rounded-full px-3 py-1 text-sm font-medium ${getSeatStatusBadgeClass(seatStatus)}`}
            >
              {formatSeatStatus(seatStatus)}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-x-8 gap-y-6">
          <Metric label="Subscription Payer" value={ownerName} />
          <Metric label="Paying Organization" value={organization} />
          <Metric label="Joined On" value={joinedDate} />
        </div>
        <div className="flex flex-col gap-4 pt-2">
          {showCreateSubscriptionButton ? (
            <>
              <Alert className="bg-primary-50 border-primary-200">
                <AlertDescription className="text-sm text-gray-700">
                  The subscription you were part of is no longer active, or your seat has been removed. To continue using Waterlily, please start your own subscription.
                </AlertDescription>
              </Alert>
              <Button
                onClick={handleCreateSubscription}
                disabled={isLoadingCheckout}
                className="flex w-fit items-center gap-2"
              >
                {isLoadingCheckout ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    <span>Start Your Subscription</span>
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <Alert className="bg-primary-50 border-primary-100">
                <AlertDescription className="text-sm text-gray-700">
                  {`Your access is provided through a subscription owned by ${organization}. Please contact support if you need assistance.`}
                </AlertDescription>
              </Alert>
              <ContactSupportButton
                subject="Non-Owner Subscription Inquiry"
                className="w-fit"
              >
                Contact Support
              </ContactSupportButton>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
